export type EmitterImpulse = { vx: number; vy: number };

export type EmitterConfig = {
  container: HTMLElement;
  origin: { x: number; y: number };
  count: number;
  spawnInterval?: number;
  charLadder: string[];
  ladderInterval: number;
  impulse: () => EmitterImpulse;
  gravity: number;
  drag: number;
  lifetime: () => number;
  jitter?: number;
  jitterFreq?: number;
  fontSize?: () => string;
  color?: string;
  delay?: number;
  rotate?: number;
  fadeInFrac?: number;
};

type Particle = {
  el: HTMLSpanElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  ladderTimer: number;
  ladderIdx: number;
  swayPhase: number;
  rotation: number;
};

export function emitAscii(config: EmitterConfig): () => void {
  const {
    container,
    origin,
    count,
    spawnInterval = 0,
    charLadder,
    ladderInterval,
    impulse,
    gravity,
    drag,
    lifetime,
    jitter = 0,
    jitterFreq = 2,
    fontSize,
    color,
    delay = 0,
    rotate = 0,
    fadeInFrac = 0.1,
  } = config;

  let cancelled = false;
  let rafId = 0;
  let spawnTimerId = 0;
  let delayTimerId = 0;
  const particles: Particle[] = [];
  let tickRunning = false;
  let lastTime = 0;

  const tick = (now: number): void => {
    if (cancelled) {
      tickRunning = false;
      return;
    }
    const dtSec = Math.min((now - lastTime) / 1000, 0.05);
    const dtMs = dtSec * 1000;
    lastTime = now;

    let anyAlive = false;

    for (const p of particles) {
      if (p.age >= p.life) continue;
      anyAlive = true;
      p.age += dtMs;

      p.vy += gravity * dtSec;
      if (drag > 0) {
        const damping = Math.exp(-drag * dtSec);
        p.vx *= damping;
        p.vy *= damping;
      }

      p.x += p.vx * dtSec;
      p.y += p.vy * dtSec;

      const sway = jitter
        ? Math.sin((p.age / 1000) * jitterFreq * Math.PI * 2 + p.swayPhase) * jitter
        : 0;
      const renderX = p.x + sway;

      const t = p.age / p.life;
      const opacity =
        t < fadeInFrac ? t / fadeInFrac : 1 - (t - fadeInFrac) / (1 - fadeInFrac);

      p.el.style.transform = `translate(${renderX}px, ${p.y}px) rotate(${p.rotation}deg)`;
      p.el.style.opacity = String(Math.max(0, opacity));

      p.ladderTimer += dtMs;
      if (p.ladderTimer >= ladderInterval && p.ladderIdx < charLadder.length - 1) {
        p.ladderTimer -= ladderInterval;
        p.ladderIdx++;
        p.el.textContent = charLadder[p.ladderIdx] ?? "";
      }

      if (p.age >= p.life) {
        p.el.remove();
      }
    }

    if (anyAlive) {
      rafId = requestAnimationFrame(tick);
    } else {
      tickRunning = false;
    }
  };

  const spawnOne = (): void => {
    if (cancelled) return;

    const el = document.createElement("span");
    el.className = "aw-particle";
    el.textContent = charLadder[0] ?? "";
    if (fontSize) el.style.fontSize = fontSize();
    if (color) el.style.color = color;

    const { vx, vy } = impulse();
    const life = lifetime();
    const rotation = rotate ? (Math.random() - 0.5) * rotate * 2 : 0;

    el.style.transform = `translate(${origin.x}px, ${origin.y}px) rotate(${rotation}deg)`;
    el.style.opacity = "0";
    container.appendChild(el);

    particles.push({
      el,
      x: origin.x,
      y: origin.y,
      vx,
      vy,
      age: 0,
      life,
      ladderTimer: 0,
      ladderIdx: 0,
      swayPhase: Math.random() * Math.PI * 2,
      rotation,
    });

    if (!tickRunning) {
      tickRunning = true;
      lastTime = performance.now();
      rafId = requestAnimationFrame(tick);
    }
  };

  const beginSpawning = (): void => {
    if (cancelled) return;
    if (spawnInterval <= 0) {
      for (let i = 0; i < count; i++) spawnOne();
      return;
    }
    let spawnedCount = 0;
    const next = (): void => {
      if (cancelled || spawnedCount >= count) return;
      spawnOne();
      spawnedCount++;
      if (spawnedCount < count) {
        spawnTimerId = window.setTimeout(next, spawnInterval);
      }
    };
    next();
  };

  if (delay > 0) {
    delayTimerId = window.setTimeout(beginSpawning, delay);
  } else {
    beginSpawning();
  }

  return () => {
    cancelled = true;
    cancelAnimationFrame(rafId);
    clearTimeout(spawnTimerId);
    clearTimeout(delayTimerId);
    for (const p of particles) p.el.remove();
    particles.length = 0;
  };
}

export function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)] as T;
}
